// The app's session store and controller, and their React provider (FR-010, FR-012, FR-017, FR-020;
// contracts/session-seam.md §1). 002 fills 001's ports with the session controller.
import { createContext, type ReactNode, useEffect } from "react";

import { configuredBaseUrl } from "@/api/baseUrl";
import { createApiClient } from "@/api/client";
import { createAuthApi } from "@/auth/authApi";
import { pendingJoin } from "@/join/pendingJoin";
import { studentsProbe } from "@/join/studentsProbe";
import { clearParentData } from "@/profile/parentDataStore";
import { appNotices } from "@/shell/notice/store";
import {
  appCurrentSchool,
  clearCurrentSchool,
} from "@/school-identity/CurrentSchoolProvider";
import { clearAdminSchool } from "@/school-identity/adminSchoolSelection";

import { activeRolePreference } from "./activeRolePreference";
import { createAppSession } from "./appSession";
import type { SessionController } from "./controller";
import { crossTab } from "./crossTab";
import { onOnline } from "./onOnline";
import { devicePlatform } from "./platform";
import { refreshCredential } from "./refreshCredential";
import { readSeed } from "./seed";
import { sessionHint } from "./sessionHint";
import type { SessionStore } from "./types";

// The auth operations are public (security: []): their client sends no bearer token and never
// triggers a renewal, so a refused renewal cannot loop.
const authClient = createApiClient({
  baseUrl: configuredBaseUrl(),
  session: { getAccessToken: () => null, onUnauthenticated: () => {} },
});

/** The auth operations, for the sign-in, sign-up and link screens (through AuthApiContext). */
export const appAuthApi = createAuthApi(authClient);

const appSession = createAppSession({
  api: appAuthApi,
  platform: devicePlatform,
  storage: { refreshCredential, sessionHint, activeRolePreference },
  pendingJoin,
  crossTab,
  netInfo: { onOnline },
  notices: appNotices,
  // E2E builds only: the seed (seed.e2e.ts). Every other build: seed.ts returns null.
  readSeed,
  onSeed: (seed) => {
    if (seed.currentSchool) appCurrentSchool.set(seed.currentSchool);
  },
  // 003: the students probe forgets the signed-in parent (studentsProbe.reset).
  // 004: the parent data store and the selected child (FR-007).
  cleaners: [
    clearCurrentSchool,
    clearAdminSchool,
    () => studentsProbe.reset(),
    clearParentData,
  ],
});

export const appSessionStore: SessionStore = appSession.store;
export const appSessionController: SessionController = appSession.controller;

export const SessionContext = createContext<SessionStore>(appSessionStore);
export const SessionControllerContext =
  createContext<SessionController>(appSessionController);

export function SessionProvider({
  children,
  store = appSessionStore,
  controller,
}: {
  children: ReactNode;
  store?: SessionStore;
  /** Tests pass the controller that belongs to their store. */
  controller?: SessionController;
}) {
  // The app controller's listeners belong to the app store only.
  const owned =
    controller ?? (store === appSessionStore ? appSessionController : null);
  useEffect(() => {
    if (store.getSnapshot().status === "restoring") void store.restore();
  }, [store]);
  useEffect(() => owned?.start(), [owned]);
  return (
    <SessionContext.Provider value={store}>
      <SessionControllerContext.Provider
        value={controller ?? appSessionController}
      >
        {children}
      </SessionControllerContext.Provider>
    </SessionContext.Provider>
  );
}
